import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import VideoPlayer from '../VideoPlayer';

type DemoState = 'reading' | 'confused' | 'ask' | 'answer';

interface StoryCard {
  id: DemoState;
  step: string;
  title: string;
  description: string;
  color: 'blue' | 'yellow' | 'purple' | 'green';
}

const storyCards: StoryCard[] = [
  {
    id: 'reading',
    step: 'Step 1',
    title: "You're deep in Chapter 4...",
    description: "Reading about carbon chemistry. You've highlighted a passage about functional groups — it seems important, but how does it all connect?",
    color: 'blue',
  },
  {
    id: 'confused',
    step: 'Step 2',
    title: "Something doesn't click.",
    description: "You've read the definition three times. The words make sense individually, but the bigger picture isn't forming. Sound familiar?",
    color: 'yellow',
  },
  {
    id: 'ask',
    step: 'Step 3',
    title: 'You ask Vidya...',
    description: '"How do functional groups affect reactivity?" You\'re expecting the usual — a textbook definition, maybe some examples you\'ve seen before.',
    color: 'purple',
  },
  {
    id: 'answer',
    step: 'Step 4',
    title: 'But this answer is different.',
    description: "It references what you highlighted. It connects to last week's reading about isomers. It builds on notes you took yesterday. This isn't a generic answer — it knows your journey.",
    color: 'green',
  },
];

const cardColors = {
  blue: 'bg-gradient-to-br from-sky-50 to-sky-100 border-sky-200',
  yellow: 'bg-gradient-to-br from-amber-50 to-yellow-100 border-yellow-300',
  purple: 'bg-gradient-to-br from-violet-50 to-purple-100 border-purple-200',
  green: 'bg-gradient-to-br from-emerald-50 to-green-100 border-green-200',
};

const StickyScrollDemo: React.FC = () => {
  return (
    <section id="demo" className="py-10 px-4 sm:px-6 lg:px-8">
      {/* Section Header */}
      <div className="text-center mb-10">
        <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold mb-4 bg-white/95 border border-gray-300 text-gray-800 shadow-md backdrop-blur">
          See it in action
        </span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
          Answers shaped by your learning.
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Watch how Vidya responds differently by grounding each explanation in what you’ve already read and understood.
        </p>
      </div>

      {/* Demo Video */}
      <VideoPlayer />

      {/* Alternating Steps */}
      <div className="max-w-6xl mx-auto space-y-16 lg:space-y-24 relative z-20">
        {storyCards.map((card, index) => {
          const isEven = index % 2 === 0;
          return (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5 }}
              className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-center`}
            >
              {/* Story Card */}
              <div className="flex-1 w-full">
                <div className={`p-8 rounded-2xl border shadow-lg ${cardColors[card.color]}`}>
                  <div className="text-xs text-gray-500 uppercase tracking-widest mb-3">
                {card.step}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">
                {card.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {card.description}
              </p>
                </div>
              </div>

              {/* App Mockup */}
              <div className="flex-1 w-full max-w-lg lg:max-w-none">
                <AppMockup state={card.id} />
              </div>
            </motion.div>
          );
        })}

        {/* Closing statement */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="p-10 lg:p-12 rounded-2xl text-center"
          style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
        >
          <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">
            This is how understanding compounds.
          </h3>
          <p className="text-white/85 text-base lg:text-lg leading-relaxed max-w-2xl mx-auto sm:whitespace-nowrap">
            Each answer connects new ideas to what you’ve already learned, so meaning builds naturally over time.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// App Mockup - renders specific state content
const AppMockup: React.FC<{ state: DemoState }> = ({ state }) => {
  return (
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
      {/* Window Header */}
      <div
        className="px-4 py-3.5 flex items-center gap-2.5"
        style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
      >
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-white/30" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/30" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/30" />
        </div>
        <span className="text-white text-sm font-semibold">
          Chemistry 2e — Chapter 4
        </span>
      </div>

      {/* Content based on state */}
      <div className="p-5 min-h-[320px]">
        {state === 'reading' && <ReadingContent />}
        {state === 'confused' && <ConfusedContent />}
        {state === 'ask' && <AskContent />}
        {state === 'answer' && <AnswerContent />}
      </div>
    </div>
  );
};

// State 1: Reading
const ReadingContent: React.FC = () => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
    <div className="text-[11px] text-gray-400 uppercase tracking-wider mb-3">
      Chapter 4: Carbon and Molecular Diversity
    </div>
    <p className="text-sm text-gray-600 leading-relaxed">
      Carbon atoms have the ability to form long chains and rings, creating the molecular "scaffold" for life.{' '}
      <span className="bg-gradient-to-r from-cyan-500/25 to-blue-500/25 px-1 py-0.5 rounded">
        Functional groups are specific clusters of atoms that attach to a molecule's carbon backbone.
      </span>{' '}
      The backbone provides structure, but the functional group is the "business end"...
    </p>
  </div>
);

// State 2: Confused
const ConfusedContent: React.FC = () => (
  <div className="relative">
    <div className="absolute -top-2 right-2 w-9 h-9 bg-amber-400 rounded-full flex items-center justify-center text-xl font-bold text-gray-900 shadow-lg z-10">
      ?
    </div>
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
      <div className="text-[11px] text-gray-400 uppercase tracking-wider mb-3">
        Chapter 4: Carbon and Molecular Diversity
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">
        Carbon atoms have the ability to form long chains and rings, creating the molecular "scaffold" for life.{' '}
        <span className="bg-gradient-to-r from-cyan-500/25 to-blue-500/25 px-1 py-0.5 rounded">
          Functional groups are specific clusters of atoms that attach to a molecule's carbon backbone.
        </span>{' '}
        The backbone provides structure, but the functional group is the "business end"...
      </p>
    </div>
  </div>
);

// State 3: Ask
const AskContent: React.FC = () => (
  <>
    <div className="bg-gray-50 border-2 border-cyan-500 rounded-xl p-4 mb-4">
      <div className="text-[11px] text-cyan-600 uppercase tracking-wider font-semibold mb-2">
        Ask Vidya
      </div>
      <div className="text-gray-900 font-medium">
        How do functional groups affect reactivity?
        <span className="inline-block w-0.5 h-4 bg-cyan-500 ml-0.5 animate-pulse align-middle" />
      </div>
    </div>
    <p className="text-xs text-gray-400">
      Answering from Biology 2e + your notes
    </p>
  </>
);

// State 4: Answer
const AnswerContent: React.FC = () => (
  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
    {/* Intro */}
    <div className="p-4 border-b border-gray-100 text-sm text-gray-900 leading-relaxed">
        That's a fantastic question! Thinking about functional groups is{' '}
        <span className="text-cyan-600 font-medium">the next perfect step after understanding isomers</span>...
    </div>

    {/* Blur lines */}
    <div className="p-3 bg-gray-50">
      <div className="h-2.5 bg-gray-200 rounded-full mb-1.5 blur-[1px]" />
      <div className="h-2.5 bg-gray-200 rounded-full w-4/5 mb-1.5 blur-[1px]" />
      <div className="h-2.5 bg-gray-200 rounded-full w-3/5 blur-[1px]" />
    </div>

    {/* Connections */}
    <div className="p-4 border-b border-gray-100">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2.5">
        <span>🔗</span> Connections
      </div>
      <p className="text-xs text-gray-600 leading-relaxed mb-2">
        <span className="text-cyan-600 font-medium">Remember how we talked about glucose and fructose being structural isomers?</span>{' '}
        The reason they're different is the position of a carbonyl functional group...
      </p>
      <span className="inline-block bg-cyan-500/10 text-cyan-600 px-2 py-0.5 rounded text-[10px] font-medium">
        Chemistry2e (Page 87)
      </span>

      <div className="mt-3 p-2.5 rounded-lg bg-gradient-to-r from-cyan-500/10 to-blue-500/10 text-center">
        <span className="text-[11px] font-semibold text-cyan-600">
          Carbon Backbone → Functional Groups → Monomers → Polymers → Life!
        </span>
      </div>
    </div>

    {/* Visual Guide */}
    <div className="p-4 bg-gradient-to-br from-emerald-50 to-green-50 border-b border-emerald-200">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 mb-2.5">
        <span>📸</span> Visual Guide
      </div>
      {/* Mini table preview */}
      <div className="bg-white rounded-lg border border-emerald-200 overflow-hidden mb-2">
        <div className="grid grid-cols-3 text-[9px] font-semibold text-gray-600 bg-emerald-100/50">
          <div className="px-2 py-1 border-r border-emerald-200">Group</div>
          <div className="px-2 py-1 border-r border-emerald-200">Structure</div>
          <div className="px-2 py-1">Properties</div>
        </div>
        <div className="grid grid-cols-3 text-[9px] text-gray-600 border-t border-emerald-100">
          <div className="px-2 py-1 border-r border-emerald-100 font-medium text-emerald-700">Hydroxyl</div>
          <div className="px-2 py-1 border-r border-emerald-100 font-mono">-OH</div>
          <div className="px-2 py-1 text-cyan-600">Polar</div>
        </div>
        <div className="grid grid-cols-3 text-[9px] text-gray-600 border-t border-emerald-100">
          <div className="px-2 py-1 border-r border-emerald-100 font-medium text-emerald-700">Carbonyl</div>
          <div className="px-2 py-1 border-r border-emerald-100 font-mono">=O</div>
          <div className="px-2 py-1 text-cyan-600">Polar</div>
          </div>
        <div className="grid grid-cols-3 text-[9px] text-gray-600 border-t border-emerald-100">
          <div className="px-2 py-1 border-r border-emerald-100 font-medium text-emerald-700">Methyl</div>
          <div className="px-2 py-1 border-r border-emerald-100 font-mono">-CH₃</div>
          <div className="px-2 py-1 text-gray-500">Nonpolar</div>
        </div>
      </div>
      <p className="text-[10px] text-emerald-800 leading-relaxed mb-1.5">
        <span className="text-emerald-700 font-medium">This is why reactivity varies:</span> polar groups like -OH interact with water, while nonpolar groups like -CH₃ repel it.
      </p>
      <span className="inline-block bg-cyan-500/10 text-cyan-600 px-2 py-0.5 rounded text-[10px] font-medium">
        Chemistry2e (Page 77)
      </span>
    </div>

    {/* Notes */}
    <div className="p-4 bg-amber-50">
      <div className="text-xs font-semibold text-amber-800 mb-2">
        📝 Your Notes
      </div>
      <p className="text-xs text-amber-900 leading-relaxed">
        <span className="font-semibold">In our last chat, you saved an insight</span> about how functional groups determine a molecule's properties...
      </p>
    </div>
  </div>
);

export default StickyScrollDemo;
